import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionMenuComponent, HoverIdComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { DeviceDowntimeItem } from '../data-access/downtime.models';

@Component({
  selector: 'app-downtime-table',
  standalone: true,
  imports: [StatusBadgeComponent, TranslatePipe, ActionMenuComponent, HoverIdComponent],
  template: `
    <div class="downtime-table">
      <table class="downtime-table__table">
        <thead>
            <tr>
              <th>{{ 'Clinic' | t }}</th>
              <th>{{ 'Device' | t }}</th>
              <th>Downtime Window</th>
              <th>{{ 'Actual End Time' | t }}</th>
              <th class="downtime-table__impact-heading">Impact</th>
            <th>{{ 'common.status' | t }}</th>
            <th>{{ 'common.actions' | t }}</th>
          </tr>
        </thead>

        <tbody>
          @for (item of items; track item.id) {
            <tr
              class="downtime-table__row"
              tabindex="0"
              (click)="onRowSelected($event, item)"
              (keydown.enter)="onRowSelected($event, item)"
              (keydown.space)="onRowSelectedFromSpace($event, item)"
            >
              <td>
                <lib-hover-id [value]="item.clinicId">
                  @if (canOpenClinic && item.clinicId.trim()) {
                    <button
                      type="button"
                      class="downtime-table__link-button"
                      (click)="$event.stopPropagation(); clinicOpenClicked.emit(item.clinicId)"
                      [title]="clinicLabel(item)"
                    >
                      {{ clinicLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="clinicLabel(item)">{{ clinicLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.deviceId">
                  @if (canOpenDevice && item.deviceId.trim()) {
                    <button
                      type="button"
                      class="downtime-table__link-button"
                      (click)="$event.stopPropagation(); deviceOpenClicked.emit(item.deviceId)"
                      [title]="deviceLabel(item)"
                    >
                      {{ deviceLabel(item) }}
                    </button>
                  } @else {
                    <span [title]="deviceLabel(item)">{{ deviceLabel(item) }}</span>
                  }
                </lib-hover-id>
              </td>
              <td>{{ formatWindow(item.startTime, item.expectedRepairUntil, item.clinicTimeZone) }}</td>
              <td>
                <div class="downtime-table__actual-end">
                  <span>{{ formatAsLocalNaiveDateTime(item.actualEndTime, item.clinicTimeZone) }}</span>
                  @if (item.isOverdue) {
                    <span class="downtime-table__overdue-badge">{{ overdueLabel(item) }}</span>
                  }
                </div>
              </td>
              <td class="downtime-table__impact-cell">
                <span
                  class="downtime-table__impact-dot"
                  [class.downtime-table__impact-dot--resolved]="isImpactResolved(item)"
                  [class.downtime-table__impact-dot--action-required]="!isImpactResolved(item)"
                  [attr.title]="impactTitle(item)"
                  [attr.aria-label]="impactTitle(item)"
                ></span>
              </td>
              <td>
                <app-status-badge
                  [label]="getStatusViewModel(item.status).label"
                  [tone]="getStatusViewModel(item.status).tone"
                />
              </td>
              <td (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
                @if (hasAvailableActions(item)) {
                  <lib-action-menu [menuTemplate]="tableActionsMenu" buttonAriaLabel="Open device downtime actions">
                    <ng-template #tableActionsMenu let-close="close">
                      @if (canManageDowntime) {
                        @if (item.status === 'active') {
                          <button
                            type="button"
                            class="action-menu__item action-menu__item--primary"
                            [disabled]="busyItemId === item.id"
                            (click)="resolveClicked.emit(item); close()"
                          >
                            Finish downtime
                          </button>
                        }

                        <button
                          type="button"
                          class="action-menu__item action-menu__item--danger"
                          [disabled]="busyItemId === item.id"
                          (click)="deleteClicked.emit(item); close()"
                        >
                          {{ 'Delete' | t }}
                        </button>
                      }

                      @if (canModerateRequests && item.status === 'pending_approval') {
                        <button
                          type="button"
                          class="action-menu__item"
                          [disabled]="busyItemId === item.id"
                          (click)="approveClicked.emit(item); close()"
                        >
                          {{ 'action.approve' | t }}
                        </button>

                        <button
                          type="button"
                          class="action-menu__item"
                          [disabled]="busyItemId === item.id"
                          (click)="rejectClicked.emit(item); close()"
                        >
                          {{ 'action.reject' | t }}
                        </button>
                      }
                    </ng-template>
                  </lib-action-menu>
                } @else {
                  <span class="downtime-table__empty-action">—</span>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="downtime-table__cards">
        @for (item of items; track item.id) {
          <article class="downtime-table__card" (click)="rowClicked.emit(item)">
            <div class="downtime-table__card-head">
              @if (canOpenDevice && item.deviceId.trim()) {
                <lib-hover-id [value]="item.deviceId">
                  <button
                    type="button"
                    class="downtime-table__link-button downtime-table__link-button--card"
                    (click)="$event.stopPropagation(); deviceOpenClicked.emit(item.deviceId)"
                    [title]="deviceLabel(item)"
                  >
                    {{ deviceLabel(item) }}
                  </button>
                </lib-hover-id>
              } @else {
                <lib-hover-id [value]="item.deviceId">
                  <div class="downtime-table__card-title">{{ deviceLabel(item) }}</div>
                </lib-hover-id>
              }
              <div class="downtime-table__card-status">
                <span
                  class="downtime-table__impact-dot"
                  [class.downtime-table__impact-dot--resolved]="isImpactResolved(item)"
                  [class.downtime-table__impact-dot--action-required]="!isImpactResolved(item)"
                  [attr.title]="impactTitle(item)"
                  [attr.aria-label]="impactTitle(item)"
                ></span>
                <app-status-badge
                  [label]="getStatusViewModel(item.status).label"
                  [tone]="getStatusViewModel(item.status).tone"
                />
              </div>
            </div>

            <div class="downtime-table__card-grid">
              <div class="downtime-table__card-row">
                <div class="downtime-table__card-label">{{ 'Clinic' | t }}</div>
                <div class="downtime-table__card-value">
                  @if (canOpenClinic && item.clinicId.trim()) {
                    <lib-hover-id [value]="item.clinicId">
                      <button
                        type="button"
                        class="downtime-table__link-button downtime-table__link-button--card"
                        (click)="$event.stopPropagation(); clinicOpenClicked.emit(item.clinicId)"
                        [title]="clinicLabel(item)"
                      >
                        {{ clinicLabel(item) }}
                      </button>
                    </lib-hover-id>
                  } @else {
                    <lib-hover-id [value]="item.clinicId">{{ clinicLabel(item) }}</lib-hover-id>
                  }
                </div>
              </div>

              <div class="downtime-table__card-row">
                <div class="downtime-table__card-label">Downtime Window</div>
                <div class="downtime-table__card-value">{{ formatWindow(item.startTime, item.expectedRepairUntil, item.clinicTimeZone) }}</div>
              </div>

              <div class="downtime-table__card-row">
                <div class="downtime-table__card-label">{{ 'Actual End Time' | t }}</div>
                <div class="downtime-table__card-value">
                  <div class="downtime-table__actual-end">
                    <span>{{ formatAsLocalNaiveDateTime(item.actualEndTime, item.clinicTimeZone) }}</span>
                    @if (item.isOverdue) {
                      <span class="downtime-table__overdue-badge">{{ overdueLabel(item) }}</span>
                    }
                  </div>
                </div>
              </div>

            </div>

            @if (canManageDowntime || (canModerateRequests && item.status === 'pending_approval')) {
              <div class="downtime-table__card-actions" (click)="$event.stopPropagation()">
                @if (canManageDowntime) {
                  @if (item.status === 'active') {
                    <button
                      type="button"
                      [disabled]="busyItemId === item.id"
                      (click)="resolveClicked.emit(item)"
                    >
                      Finish downtime
                    </button>
                  }

                  <button
                    type="button"
                    [disabled]="busyItemId === item.id"
                    (click)="deleteClicked.emit(item)"
                  >
                    {{ 'Delete' | t }}
                  </button>
                }

                @if (canModerateRequests && item.status === 'pending_approval') {
                  <button
                    type="button"
                    [disabled]="busyItemId === item.id"
                    (click)="approveClicked.emit(item)"
                  >
                    {{ 'action.approve' | t }}
                  </button>

                  <button
                    type="button"
                    [disabled]="busyItemId === item.id"
                    (click)="rejectClicked.emit(item)"
                  >
                    {{ 'action.reject' | t }}
                  </button>
                }
              </div>
            }
          </article>
        }
      </div>
    </div>
  `,
  styles: [`
    .downtime-table {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow-x: auto;
    }

    .downtime-table__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 980px;
    }

    .downtime-table__cards {
      display: none;
    }

    .downtime-table__table th,
    .downtime-table__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
      font-size: 13px;
    }

    .downtime-table__table th {
      background: #f9fafb;
      color: #374151;
    }

    .downtime-table__table td {
      color: #111827;
    }

    .downtime-table__impact-heading,
    .downtime-table__impact-cell {
      width: 64px;
      text-align: center;
    }

    .downtime-table__impact-dot {
      display: inline-flex;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      border: 2px solid #ffffff;
      box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.16);
      vertical-align: middle;
    }

    .downtime-table__impact-dot--resolved {
      background: #22c55e;
    }

    .downtime-table__impact-dot--action-required {
      background: #ef4444;
    }

    .downtime-table__actual-end {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .downtime-table__overdue-badge {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      border: 1px solid #fecaca;
      border-radius: 999px;
      background: #fef2f2;
      color: #991b1b;
      padding: 0 8px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
    }

    .downtime-table__row {
      cursor: pointer;
      transition: background-color 140ms ease;
    }

    .downtime-table__row:hover,
    .downtime-table__row:focus-visible {
      background: #f8fafc;
      outline: none;
    }

    .downtime-table__empty-action {
      color: #94a3b8;
    }

    .downtime-table__link-button {
      border: none;
      padding: 0;
      background: transparent;
      color: #2563eb;
      font: inherit;
      text-align: left;
      cursor: pointer;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }

    .downtime-table__link-button--card {
      font-size: 16px;
      font-weight: 700;
    }

    .downtime-table__card-actions button:last-child {
      border-color: #fecaca;
      background: linear-gradient(180deg, #ffffff 0%, #fef2f2 100%);
      color: #b91c1c;
    }

    .downtime-table__card {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
      cursor: pointer;
    }

    .downtime-table__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .downtime-table__card-status {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .downtime-table__card-title {
      color: #111827;
      font-size: 15px;
      font-weight: 700;
      overflow-wrap: anywhere;
    }

    .downtime-table__card-grid {
      display: grid;
      gap: 10px;
    }

    .downtime-table__card-row {
      display: grid;
      grid-template-columns: minmax(0, 120px) minmax(0, 1fr);
      gap: 10px;
      align-items: start;
    }

    .downtime-table__card-label {
      color: #64748b;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .downtime-table__card-value {
      color: #111827;
      font-size: 13px;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }

    .downtime-table__card-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      padding-top: 6px;
      border-top: 1px solid #f1f5f9;
    }

    .downtime-table__card-actions button {
      min-height: 36px;
      border-radius: 10px;
      border: 1px solid #dbe2ea;
      background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
      color: #1f2937;
      padding: 0 12px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.01em;
      cursor: pointer;
      transition: border-color 140ms ease, background-color 140ms ease, color 140ms ease, box-shadow 140ms ease;
    }

    .downtime-table__card-actions button:hover {
      border-color: #93c5fd;
      background: linear-gradient(180deg, #ffffff 0%, #eff6ff 100%);
      color: #1d4ed8;
      box-shadow: 0 10px 18px rgba(37, 99, 235, 0.12);
    }

    .downtime-table__card-actions button:disabled {
      opacity: 0.5;
      cursor: default;
      box-shadow: none;
    }

    @media (max-width: 900px) {
      .downtime-table {
        padding: 10px;
        display: grid;
        gap: 12px;
        background: #f8fafc;
      }

      .downtime-table__table {
        display: none;
      }

      .downtime-table__cards {
        display: grid;
        gap: 12px;
      }
    }

    @media (max-width: 640px) {
      .downtime-table__card-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }

      .downtime-table__card-actions {
        flex-direction: column;
      }

      .downtime-table__card-actions button {
        width: 100%;
      }
    }
  `]
})
export class DowntimeTableComponent
{
  @Input()
  public items: DeviceDowntimeItem[] = [];

  @Input()
  public clinics: Array<{ id: string; label: string }> = [];

  @Input()
  public devices: Array<{ id: string; label: string }> = [];

  @Input()
  public busyItemId: string | null = null;

  @Input()
  public canModerateRequests = false;

  @Input()
  public canManageDowntime = false;

  @Input()
  public canOpenDevice = false;

  @Input()
  public canOpenClinic = false;

  @Output()
  public readonly deleteClicked = new EventEmitter<DeviceDowntimeItem>();

  @Output()
  public readonly resolveClicked = new EventEmitter<DeviceDowntimeItem>();

  @Output()
  public readonly approveClicked = new EventEmitter<DeviceDowntimeItem>();

  @Output()
  public readonly rejectClicked = new EventEmitter<DeviceDowntimeItem>();

  @Output()
  public readonly deviceOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly clinicOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly rowClicked = new EventEmitter<DeviceDowntimeItem>();

  protected formatAsLocalNaiveDateTime(value: string | null, timeZone?: string | null): string
  {
    if (!value)
    {
      return '—';
    }

    const normalized = value.trim();
    if (/[zZ]|[+-]\d{2}:\d{2}$/.test(normalized))
    {
      const date = new Date(normalized);
      if (!Number.isNaN(date.getTime()))
      {
        const formatted = this.formatDateInTimeZone(date, timeZone);
        if (formatted)
        {
          return formatted;
        }
      }
    }

    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/
    );

    if (!match)
    {
      return normalized;
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  private formatDateInTimeZone(date: Date, timeZone?: string | null): string | null
  {
    try
    {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timeZone?.trim() || undefined
      });
      const parts = formatter.formatToParts(date);
      const value = (type: string): string =>
        parts.find((part) => part.type === type)?.value ?? '';

      return `${value('day')}.${value('month')}.${value('year')} ${value('hour')}:${value('minute')}`;
    }
    catch
    {
      return null;
    }
  }

  protected getStatusViewModel(status: DeviceDowntimeItem['status']): StatusBadgeViewModel
  {
    switch (status)
    {
      case 'pending_approval':
        return { label: 'status.pendingApproval', tone: 'warning' };

      case 'active':
        return { label: 'status.active', tone: 'danger' };

      case 'resolved':
        return { label: 'status.resolved', tone: 'success' };

      case 'rejected':
        return { label: 'status.rejected', tone: 'neutral' };

      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected isImpactResolved(item: DeviceDowntimeItem): boolean
  {
    return item.impactResolution?.status === 'resolved';
  }

  protected impactTitle(item: DeviceDowntimeItem): string
  {
    return this.isImpactResolved(item) ? 'Impact resolved' : 'Impact action required';
  }

  protected overdueLabel(item: DeviceDowntimeItem): string
  {
    const minutes = item.overdueByMinutes ?? 0;
    if (!item.isOverdue || minutes <= 0)
    {
      return 'Overdue';
    }

    if (minutes < 60)
    {
      return `Overdue ${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `Overdue ${hours}h ${remainingMinutes}m`
      : `Overdue ${hours}h`;
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }

  protected hasAvailableActions(item: DeviceDowntimeItem): boolean
  {
    return this.canManageDowntime || (this.canModerateRequests && item.status === 'pending_approval');
  }

  protected onRowSelected(event: Event, item: DeviceDowntimeItem): void
  {
    if (this.isInteractiveEvent(event)) {
      return;
    }

    this.rowClicked.emit(item);
  }

  protected onRowSelectedFromSpace(event: Event, item: DeviceDowntimeItem): void
  {
    event.preventDefault();
    this.onRowSelected(event, item);
  }

  protected formatWindow(
    startTime: string | null,
    expectedRepairUntil: string | null,
    timeZone?: string | null,
  ): string
  {
    return `${this.formatAsLocalNaiveDateTime(startTime, timeZone)} -> ${this.formatAsLocalNaiveDateTime(expectedRepairUntil, timeZone)}`;
  }

  protected clinicLabel(item: DeviceDowntimeItem): string
  {
    return item.clinicName?.trim()
      || this.clinics.find((clinic) => clinic.id === item.clinicId)?.label
      || this.readableId(item.clinicId);
  }

  protected deviceLabel(item: DeviceDowntimeItem): string
  {
    return item.deviceLabel?.trim()
      || this.devices.find((device) => device.id === item.deviceId)?.label
      || this.readableId(item.deviceId);
  }

  private isInteractiveEvent(event: Event): boolean
  {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, a, input, select, textarea, lib-action-menu');
  }
}
